import { useRef, useState } from 'react'
import type { Control, FieldPath, FieldValues, UseFormSetValue } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { TextInputField } from '@/components/forms/FormComponents'
import { toastHelpers } from '@/lib/toast-helpers'
import { fileToDataUrl } from '@/lib/imageDownscale'
import { ImageIcon } from 'lucide-react'

/** Raw file size cap before readAsDataURL (aligned with productImageUrlSchema in form-schemas) */
export const MAX_MENU_PHOTO_FILE_BYTES = 5 * 1024 * 1024

/**
 * The one path from a picked menu photo to the data: URL the product payload
 * carries — used by the main photo and the extra photos alike. Files over
 * MAX_MENU_PHOTO_FILE_BYTES are refused with a toast (null); the rest are
 * downscaled to a 1200 px long edge / JPEG before they are posted — a 2 MB
 * phone PNG was a 3 MB PUT that slow venue links never finished.
 */
export async function menuPhotoFileToDataUrl(f: File): Promise<string | null> {
  if (f.size > MAX_MENU_PHOTO_FILE_BYTES) {
    toastHelpers.error(
      'Image too large',
      'Please use a file under 5MB (try exporting or compressing the photo), or paste an HTTPS link instead.',
    )
    return null
  }
  const url = await fileToDataUrl(f)
  return url || null
}

/**
 * One picture and the verbs that act on it. The file is downscaled
 * (lib/imageDownscale) and becomes a data: URL held in the `image_url` field,
 * posted inline with the product; the backend rehosts it on Cloudinary when
 * the tenant has `CLOUDINARY_URL`. The URL input is still there, one link
 * away, for hosted images.
 */
export function ProductPhotoField<T extends FieldValues>({
  control,
  name,
  imageUrl,
  setValue,
}: {
  control: Control<T>
  name: FieldPath<T>
  imageUrl: string
  setValue: UseFormSetValue<T>
}) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const isDataUrl = imageUrl.startsWith('data:image')
  const isLink = imageUrl.startsWith('http')
  // The URL field stays folded until asked for. Most products carry a hosted
  // image, so opening it on `isLink` would put it back on nearly every screen —
  // the row this layout exists to remove. The picture itself is the feedback.
  const [linkOpen, setLinkOpen] = useState(false)
  const showLink = !isDataUrl && linkOpen

  const set = (v: string) =>
    setValue(name, v as never, { shouldValidate: true, shouldDirty: true })

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-4">
        {isDataUrl || isLink ? (
          <img src={imageUrl} alt="" className="h-[88px] w-[88px] shrink-0 rounded-lg border object-cover" />
        ) : (
          <div className="flex h-[88px] w-[88px] shrink-0 items-center justify-center rounded-lg border border-dashed bg-muted/40 text-muted-foreground">
            <ImageIcon className="h-6 w-6" aria-hidden />
          </div>
        )}
        <div className="space-y-1.5 text-sm">
          <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
            {imageUrl ? 'Change photo' : 'Add photo'}
          </Button>
          <div className="flex flex-wrap gap-x-3 text-muted-foreground">
            <button
              type="button"
              className="underline underline-offset-2 hover:text-foreground"
              onClick={() => setLinkOpen((v) => !v)}
            >
              {showLink ? 'Hide image link' : isLink ? 'Edit image link' : 'Paste an image link'}
            </button>
            {imageUrl && (
              <button
                type="button"
                className="underline underline-offset-2 hover:text-foreground"
                onClick={() => {
                  set('')
                  if (fileInputRef.current) fileInputRef.current.value = ''
                }}
              >
                Remove
              </button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">JPG, PNG, WebP or GIF, up to 5 MB.</p>
        </div>
      </div>
      <input
        ref={fileInputRef}
        id="product-image-file"
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
        className="sr-only"
        aria-label="Upload a product photo"
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (!f) return
          void menuPhotoFileToDataUrl(f).then((url) => {
            if (url) set(url)
            else e.target.value = ''
          })
        }}
      />
      {showLink && (
        <TextInputField
          control={control}
          name={name}
          label="Image link"
          placeholder="https://example.com/image.jpg"
        />
      )}
    </div>
  )
}
